import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MenuOutlined as MenuOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { CLINIC_DOCS_LAYOUT } from '../constants/layout';
import {
  useDeleteClinicDocumentMutation,
  useGetClinicDocumentQuery,
  useGetClinicDocumentSectionsQuery,
  useListClinicDocumentsQuery,
  useUploadClinicDocumentsMutation,
} from '../store';
import type { ClinicDocumentCardResponse } from '../types';

import { ClinicDocumentChat } from './ClinicDocumentChat';
import { ClinicDocumentDropZone } from './ClinicDocumentDropZone';
import { ClinicDocumentFileViewer } from './ClinicDocumentFileViewer';
import { ClinicDocumentSectionTreeItem } from './ClinicDocumentSectionTreeItem';
import { ClinicDocumentsSidebar } from './ClinicDocumentsSidebar';
import { DocumentMarkdownRenderer } from './DocumentMarkdownRenderer';

const PAGE_SIZE = 12;

const EMPTY_DOCUMENTS: ClinicDocumentCardResponse[] = [];

const FAST_POLL_MS = 2000;

const hasMetadata = (doc: ClinicDocumentCardResponse): boolean => doc.title !== null;

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatPageLabel = (count: number | null): string | null =>
  count === null ? null : `${count} page${count === 1 ? '' : 's'}`;

const isTerminalStatus = (status: string): boolean =>
  status === 'processed' || status === 'failed';

const getMutationErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object' &&
    'message' in error.data &&
    typeof error.data.message === 'string'
  ) {
    return error.data.message;
  }

  return fallbackMessage;
};

const getStatusChipProps = (
  status: string,
): { color: 'default' | 'primary' | 'success' | 'error' | 'warning'; label: string } => {
  switch (status) {
    case 'processed':
      return { color: 'success', label: 'Processed' };
    case 'failed':
      return { color: 'error', label: 'Failed' };
    case 'processing':
      return { color: 'warning', label: 'Processing' };
    case 'metadata_ready':
      return { color: 'primary', label: 'Metadata ready' };
    case 'uploaded':
      return { color: 'default', label: 'Uploaded' };
    default:
      return { color: 'default', label: status.replace(/_/g, ' ') };
  }
};

const dedupeDocuments = (documents: ClinicDocumentCardResponse[]): ClinicDocumentCardResponse[] => {
  const next = new Map<string, ClinicDocumentCardResponse>();
  documents.forEach(document => {
    next.set(document.clinic_document_id, document);
  });
  return [...next.values()];
};

const matchesFilters = (
  document: ClinicDocumentCardResponse,
  docKindFilter: string,
  statusFilter: string,
): boolean => {
  if (docKindFilter !== 'all' && document.doc_kind !== docKindFilter) {
    return false;
  }

  if (statusFilter !== 'all' && document.processing_status !== statusFilter) {
    return false;
  }

  return true;
};

interface ClinicDocumentDetailPanelProps {
  document: ClinicDocumentCardResponse | null;
  effectiveStatus: string;
  onDelete: (document: ClinicDocumentCardResponse) => void;
  dropZone: React.ReactNode;
}

function ClinicDocumentDetailPanel({
  document,
  effectiveStatus,
  onDelete,
  dropZone,
}: ClinicDocumentDetailPanelProps) {
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const { clinicDocuments: trackClinicDocs } = useEventTracking();
  const clinicDocumentId = document?.clinic_document_id;

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isFetching: isDetailFetching,
    error: detailError,
  } = useGetClinicDocumentQuery(
    { clinicDocumentId: clinicDocumentId ?? '' },
    {
      skip: !clinicDocumentId,
      pollingInterval:
        clinicDocumentId && !isTerminalStatus(effectiveStatus) ? FAST_POLL_MS : undefined,
    },
  );

  const effectiveDocument = detailData ?? document;

  const {
    data: sectionsData,
    isLoading: isSectionsLoading,
    error: sectionsError,
  } = useGetClinicDocumentSectionsQuery(
    { clinicDocumentId: clinicDocumentId ?? '' },
    {
      skip: !clinicDocumentId || effectiveStatus !== 'processed',
    },
  );

  if (!document) {
    return <>{dropZone}</>;
  }

  if (isDetailLoading && !effectiveDocument) {
    return (
      <Stack direction="row" spacing={1.5} sx={{alignItems: "center",  p: 4 }}>
        <CircularProgress size={22} />
        <Typography>Loading document details...</Typography>
      </Stack>
    );
  }

  if (detailError && !effectiveDocument) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load the selected document.</Alert>
      </Box>
    );
  }

  if (!effectiveDocument) {
    return null;
  }

  const statusChip = getStatusChipProps(effectiveStatus);

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2} sx={{ justifyContent: "space-between", alignItems: { xs: 'flex-start', md: 'center' } }}
      >
        <Box>
          <Stack direction="row" spacing={1} sx={{alignItems: "center",  mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {effectiveDocument.title || effectiveDocument.filename}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {effectiveDocument.filename}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Chip color={statusChip.color} label={statusChip.label} />
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => {
              setFileViewerOpen(true);
              if (clinicDocumentId) {
                trackClinicDocs.fileViewed({
                  clinic_document_id: clinicDocumentId,
                  content_type: effectiveDocument?.content_type ?? 'unknown',
                });
              }
            }}
          >
            View original
          </Button>
          <Button color="error" variant="outlined" onClick={() => onDelete(effectiveDocument)}>
            Delete
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        {effectiveDocument.doc_kind && effectiveDocument.doc_kind !== 'other' && (
          <Chip label={effectiveDocument.doc_kind.replace(/_/g, ' ')} variant="outlined" />
        )}
        <Chip label={formatBytes(effectiveDocument.size_bytes)} variant="outlined" />
        {formatPageLabel(effectiveDocument.page_count) && (
          <Chip label={formatPageLabel(effectiveDocument.page_count)} variant="outlined" />
        )}
        <Chip
          label={`Updated ${formatDateTime(effectiveDocument.updated_at)}`}
          variant="outlined"
        />
      </Stack>

      {!hasMetadata(effectiveDocument) && effectiveStatus === 'uploaded' ? (
        <Stack spacing={1}>
          <Skeleton variant="text"  sx={{ width: "90%" }}/>
          <Skeleton variant="text"  sx={{ width: "75%" }}/>
          <Skeleton variant="text"  sx={{ width: "40%" }}/>
        </Stack>
      ) : (
        <>
          {effectiveDocument.description && (
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {effectiveDocument.description}
            </Typography>
          )}

          {effectiveDocument.tags.length > 0 && (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
              {effectiveDocument.tags.map(tag => (
                <Chip key={tag} size="small" label={tag} />
              ))}
            </Stack>
          )}
        </>
      )}

      <Divider />

      {effectiveStatus === 'failed' && (
        <Alert severity="error">
          Processing failed for this document. Card metadata may still be available, but section
          browsing is unavailable.
        </Alert>
      )}

      {effectiveStatus === 'uploaded' && (
        <Alert severity="info">
          Document uploaded. Generating metadata (title, description, tags) — this usually takes
          about 5 seconds.
        </Alert>
      )}

      {effectiveStatus === 'metadata_ready' && (
        <Alert severity="info">
          Metadata is ready. Deep parsing is running in the background and usually finishes within
          30 seconds to 2 minutes.
        </Alert>
      )}

      {effectiveStatus === 'processing' && (
        <Alert severity="info">
          Deep parsing is in progress. Section browsing will be available once it completes.
        </Alert>
      )}

      {detailData?.outline_md && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Outline preview
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
            <DocumentMarkdownRenderer markdown={detailData.outline_md} />
          </Paper>
        </Box>
      )}

      {effectiveStatus === 'processed' && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Sections
          </Typography>
          {isSectionsLoading ? (
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading top-level sections...
              </Typography>
            </Stack>
          ) : sectionsError ? (
            <Alert severity="error">Failed to load document sections.</Alert>
          ) : sectionsData?.sections.length ? (
            <Stack spacing={1.5}>
              {sectionsData.sections.map(section => (
                <ClinicDocumentSectionTreeItem
                  key={section.clinic_document_section_id}
                  section={section}
                />
              ))}
            </Stack>
          ) : (
            <Alert severity="info">
              Parsing completed, but no section tree was returned for this document.
            </Alert>
          )}
        </Box>
      )}

      <ClinicDocumentFileViewer
        open={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
        clinicDocumentId={clinicDocumentId ?? null}
        contentType={effectiveDocument.content_type}
        filename={effectiveDocument.filename}
        title={effectiveDocument.title}
      />
    </Stack>
  );
}

export default function ClinicDocumentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { clinicDocuments: trackClinicDocs, error: trackError } = useEventTracking();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'chat'>('details');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [docKindFilter, setDocKindFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [offset, setOffset] = useState(0);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [uploadedDocuments, setUploadedDocuments] = useState<ClinicDocumentCardResponse[]>([]);
  const [polledStatuses, setPolledStatuses] = useState<Record<string, string>>({});
  const settledDocumentsRef = useRef<Record<string, string>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<string | number>('100%');

  useEffect(() => {
    const updateHeight = () => {
      if (!containerRef.current) return;
      const top = containerRef.current.getBoundingClientRect().top;
      setContainerHeight(`calc(100dvh - ${top}px)`);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const [uploadClinicDocuments, { isLoading: isUploading }] = useUploadClinicDocumentsMutation();
  const [deleteClinicDocument, { isLoading: isDeleting }] = useDeleteClinicDocumentMutation();

  const {
    data: listData,
    isLoading: isListLoading,
    isFetching: isListFetching,
    error: listError,
    refetch: refetchList,
  } = useListClinicDocumentsQuery({
    docKind: docKindFilter === 'all' ? undefined : docKindFilter,
    processingStatus: statusFilter === 'all' ? undefined : statusFilter,
    search: searchQuery || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const serverDocuments = listData?.documents ?? EMPTY_DOCUMENTS;

  useEffect(() => {
    if (!serverDocuments.length) {
      return;
    }

    const serverIds = new Set(serverDocuments.map(document => document.clinic_document_id));
    setUploadedDocuments(previous =>
      previous.filter(document => !serverIds.has(document.clinic_document_id)),
    );
  }, [serverDocuments]);

  const visibleUploadedDocuments = useMemo(
    () =>
      uploadedDocuments.filter(document =>
        matchesFilters(document, docKindFilter, statusFilter),
      ),
    [docKindFilter, statusFilter, uploadedDocuments],
  );

  const documents = useMemo(
    () => dedupeDocuments([...visibleUploadedDocuments, ...serverDocuments]),
    [serverDocuments, visibleUploadedDocuments],
  );

  const totalDocuments = listData?.total ?? 0;
  const hasNextPage = offset + PAGE_SIZE < totalDocuments;
  const selectedDocument =
    documents.find(document => document.clinic_document_id === selectedDocumentId) ?? null;
  const selectedDocumentEffectiveStatus =
    (selectedDocumentId && polledStatuses[selectedDocumentId]) ||
    selectedDocument?.processing_status ||
    'uploaded';

  useEffect(() => {
    if (!documents.length) {
      setSelectedDocumentId(null);
      return;
    }

    if (
      !selectedDocumentId ||
      !documents.some(document => document.clinic_document_id === selectedDocumentId)
    ) {
      setSelectedDocumentId(documents[0].clinic_document_id);
    }
  }, [documents, selectedDocumentId]);

  const resetPagination = useCallback(() => {
    setOffset(0);
  }, []);

  const handleFileSelection = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files ?? []).filter(
      file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'),
    );
    setSelectedFiles(nextFiles);
  }, []);

  const handleDropZoneFiles = useCallback(
    async (files: File[]) => {
      const formData = new FormData();
      files.forEach(file => formData.append('files[]', file));
      formData.append('scope_type', 'organization');

      try {
        const response = await uploadClinicDocuments({ body: formData }).unwrap();
        setUploadedDocuments(previous => dedupeDocuments([...response.documents, ...previous]));
        setDocKindFilter('all');
        setStatusFilter('all');
        setOffset(0);
        setSelectedDocumentId(response.documents[0]?.clinic_document_id ?? null);
        await refetchList();

        trackClinicDocs.uploaded({
          document_count: response.documents.length,
          scope_type: 'organization',
          source: 'drop_zone',
        });

        enqueueSnackbar(
          response.documents.length === 1
            ? 'Document uploaded — metadata will appear shortly.'
            : `${response.documents.length} documents uploaded — metadata will appear shortly.`,
          { variant: 'success' },
        );
      } catch (err: unknown) {
        trackClinicDocs.uploadFailed({
          document_count: files.length,
          scope_type: 'organization',
          error_message: getMutationErrorMessage(err, 'Unknown error'),
          source: 'drop_zone',
        });
        enqueueSnackbar(getMutationErrorMessage(err, 'Failed to upload clinic documents.'), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, refetchList, trackClinicDocs, uploadClinicDocuments],
  );

  const handleUpload = useCallback(async () => {
    if (!selectedFiles.length) {
      enqueueSnackbar('Choose at least one PDF to upload.', { variant: 'warning' });
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('files[]', file);
    });
    formData.append('scope_type', 'organization');

    try {
      const response = await uploadClinicDocuments({ body: formData }).unwrap();
      setUploadedDocuments(previous => dedupeDocuments([...response.documents, ...previous]));
      setDocKindFilter('all');
      setStatusFilter('all');
      setOffset(0);
      setSelectedDocumentId(response.documents[0]?.clinic_document_id ?? null);
      setSelectedFiles([]);
      await refetchList();

      trackClinicDocs.uploaded({
        document_count: response.documents.length,
        scope_type: 'organization',
        source: 'sidebar',
      });

      enqueueSnackbar(
        response.documents.length === 1
          ? 'Document uploaded — metadata will appear shortly.'
          : `${response.documents.length} documents uploaded — metadata will appear shortly.`,
        { variant: 'success' },
      );
    } catch (err: unknown) {
      trackClinicDocs.uploadFailed({
        document_count: selectedFiles.length,
        scope_type: 'organization',
        error_message: getMutationErrorMessage(err, 'Unknown error'),
        source: 'sidebar',
      });
      enqueueSnackbar(getMutationErrorMessage(err, 'Failed to upload clinic documents.'), {
        variant: 'error',
      });
    }
  }, [
    enqueueSnackbar,
    refetchList,
    selectedFiles,
    trackClinicDocs,
    uploadClinicDocuments,
  ]);

  const handleDelete = useCallback(
    async (document: ClinicDocumentCardResponse) => {
      const shouldDelete = window.confirm(
        `Delete "${document.title || document.filename}"? This cannot be undone.`,
      );

      if (!shouldDelete) {
        return;
      }

      try {
        await deleteClinicDocument({
          clinicDocumentId: document.clinic_document_id,
        }).unwrap();
        setUploadedDocuments(previous =>
          previous.filter(item => item.clinic_document_id !== document.clinic_document_id),
        );
        await refetchList();

        trackClinicDocs.deleted({
          clinic_document_id: document.clinic_document_id,
          scope_type: document.scope_type,
        });

        enqueueSnackbar('Document deleted.', { variant: 'success' });
      } catch (err: unknown) {
        trackError.track(getMutationErrorMessage(err, 'Unknown error'), 'clinic_doc_delete');
        enqueueSnackbar(getMutationErrorMessage(err, 'Failed to delete document.'), {
          variant: 'error',
        });
      }
    },
    [deleteClinicDocument, enqueueSnackbar, refetchList, trackClinicDocs, trackError],
  );

  const handleStatusChange = useCallback(
    (documentId: string, nextStatus: string, nextTitle: string | null) => {
      setPolledStatuses(previous => ({ ...previous, [documentId]: nextStatus }));

      setUploadedDocuments(previous =>
        previous.map(document =>
          document.clinic_document_id === documentId
            ? {
                ...document,
                processing_status: nextStatus,
                title: nextTitle ?? document.title,
              }
            : document,
        ),
      );

      if (nextStatus === 'metadata_ready' || isTerminalStatus(nextStatus)) {
        if (settledDocumentsRef.current[documentId] === nextStatus) {
          return;
        }
        settledDocumentsRef.current[documentId] = nextStatus;
        void refetchList();
      } else {
        const { [documentId]: _, ...rest } = settledDocumentsRef.current;
        settledDocumentsRef.current = rest;
      }
    },
    [refetchList],
  );

  const handleSelectDocument = useCallback(
    (documentId: string) => {
      setSelectedDocumentId(documentId);
      if (isMobile) {
        setSidebarOpen(false);
      }
    },
    [isMobile],
  );

  const dropZone = (
    <ClinicDocumentDropZone
      onFilesDropped={handleDropZoneFiles}
      isUploading={isUploading}
    />
  );

  const sidebarContent = (
    <ClinicDocumentsSidebar
      documents={documents}
      selectedDocumentId={selectedDocumentId}
      isLoading={isListLoading || !!listError}
      isFetching={isListFetching}
      hasNextPage={hasNextPage}
      total={totalDocuments}
      offset={offset}
      pageSize={PAGE_SIZE}
      searchQuery={searchQuery}
      docKindFilter={docKindFilter}
      statusFilter={statusFilter}
      selectedFiles={selectedFiles}
      isUploading={isUploading}
      onSelectDocument={handleSelectDocument}
      onStatusChange={handleStatusChange}
      onSearchChange={value => {
        setSearchQuery(value);
        resetPagination();
      }}
      onDocKindFilterChange={value => {
        setDocKindFilter(value);
        resetPagination();
      }}
      onStatusFilterChange={value => {
        setStatusFilter(value);
        resetPagination();
      }}
      onClearFilters={() => {
        setSearchQuery('');
        setDocKindFilter('all');
        setStatusFilter('all');
        resetPagination();
      }}
      onNextPage={() => setOffset(current => current + PAGE_SIZE)}
      onPreviousPage={() => setOffset(current => Math.max(0, current - PAGE_SIZE))}
      onFileSelection={handleFileSelection}
      onUpload={handleUpload}
      onRefresh={() => refetchList()}
    />
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        height: containerHeight,
        overflow: 'hidden',
      }}
    >
      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Box
          sx={{
            flexShrink: 0,
            width: CLINIC_DOCS_LAYOUT.SIDEBAR_WIDTH,
            height: '100%',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Sidebar - Mobile Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)} slotProps={{ paper: { sx: { width: CLINIC_DOCS_LAYOUT.SIDEBAR_WIDTH } } }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Main content area with tabs */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Mobile header */}
        {isMobile && (
          <Stack direction="row" spacing={1} sx={{alignItems: "center",  px: 2, pt: 2 }}>
            <IconButton
              onClick={() => setSidebarOpen(true)}
              aria-label="Open document list"
            >
              <MenuOutlinedIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Documents
            </Typography>
          </Stack>
        )}

        {/* Tab navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 2, md: 3 } }}>
          <Tabs
            value={activeTab}
            onChange={(_e, value: 'details' | 'chat') => {
              setActiveTab(value);
              trackClinicDocs.tabSwitched({ tab: value });
            }}
            aria-label="Document view tabs"
          >
            <Tab label="Document Details" value="details" />
            <Tab label="Document Q&A" value="chat" />
          </Tabs>
        </Box>

        {/* Tab content */}
        {activeTab === 'details' && (
          <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
            <ClinicDocumentDetailPanel
              document={selectedDocument}
              effectiveStatus={selectedDocumentEffectiveStatus}
              onDelete={handleDelete}
              dropZone={dropZone}
            />
            {isDeleting && (
              <Stack direction="row" spacing={1.5} sx={{alignItems: "center",  mt: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Removing document...
                </Typography>
              </Stack>
            )}
          </Box>
        )}

        {activeTab === 'chat' && (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <ClinicDocumentChat />
          </Box>
        )}
      </Box>
    </Box>
  );
}
